import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { getSupabaseClient } from '@/src/lib/supabase';
import { useAuth } from '@/src/contexts/AuthContext';
import { toast } from 'sonner';
import { ReportActionButtons } from './ReportActionButtons';

export function PurchaseReportView({ onBack }: { onBack?: () => void }) {
  const { currentTenantId, activeBranchId } = useAuth();
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [supplierMap, setSupplierMap] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, [currentTenantId, startDate, endDate]);



  const fetchData = async () => {
    if (!currentTenantId) return;
    setLoading(true);
    const supabase = getSupabaseClient();
    if (!supabase) return;
    try {
      let q = supabase
        .from('purchase_invoices')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .gte('invoice_date', startDate)
        .lte('invoice_date', endDate)
        .order('invoice_date', { ascending: false });
      if (activeBranchId) q = q.eq('branch_id', activeBranchId);
      const { data, error } = await q;
      if (error) throw error;
      setOrders(data || []);

      const supplierIds = [...new Set((data || []).map((o: any) => o.vendor_id).filter(Boolean))];
      if (supplierIds.length > 0) {
        const { data: supData } = await supabase.from('suppliers').select('id, supplier_name').in('id', supplierIds);
        const map: Record<string, string> = {};
        (supData || []).forEach((s: any) => { map[s.id] = s.supplier_name; });
        setSupplierMap(map);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch purchase report');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = orders.reduce((s, o) => s + (o.total_amount || 0), 0);

  const reportRows = orders.map((o: any) => ({
    'Invoice Number': o.invoice_number,
    'Date': o.invoice_date,
    'Supplier': supplierMap[o.vendor_id] || 'Unknown',
    'Status': 'Recorded',
    'Amount': Number(o.total_amount || 0)
  }));

  const reportColumns = [
    { key: 'Invoice Number', label: 'Invoice Number' },
    { key: 'Date', label: 'Date' },
    { key: 'Supplier', label: 'Supplier' },
    { key: 'Status', label: 'Status' },
    { key: 'Amount', label: 'Amount', align: 'right' as const }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Purchase Report</h2>
          <p className="text-slate-500 dark:text-slate-400">All purchase orders for the selected period.</p>
        </div>
        <ReportActionButtons
          data={reportRows}
          columns={reportColumns}
          filename={`purchase_report_${startDate}_to_${endDate}`}
          title="Purchase Report"
          subtitle={`${startDate} to ${endDate}`}
        />
      </div>

      <Card className="border-none shadow-sm">
        <CardContent className="p-4 flex flex-wrap gap-4 items-end">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Start Date</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">End Date</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <Button onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Orders</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{orders.length}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Purchase Value</p>
            <p className="text-2xl font-bold text-primary mt-1">₹{totalAmount.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900 border-b">
              <tr>
                <th className="px-6 py-4 font-semibold">PO Number</th>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold">Supplier</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">Loading report...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">No purchase orders found for this period.</td></tr>
              ) : (
                orders.map((o: any) => (
                  <tr key={o.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">
                      <div className="flex items-center"><ShoppingBag className="h-4 w-4 mr-2 text-slate-400" />{o.po_number}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{o.po_date}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{supplierMap[o.supplier_id] || 'Unknown'}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full uppercase bg-slate-100 text-slate-700">{o.status}</span>
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-slate-900 dark:text-slate-100">₹{o.total_amount?.toLocaleString('en-IN')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
